import { QuestionnaireQuestion, QuestionnaireAnswers, QuestionnaireRule } from "../types/questionnaire";
import { getNoneOfTheAboveValues, isOnlyNoneOfTheAboveSelected } from "./none-of-the-above";

function normalizeAnswerToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** Match MDI rule answers across Yes/Sí/Si and exact strings. */
function answerMatchesRequired(
  answer: string | boolean,
  requiredAnswer: string
): boolean {
  if (typeof answer === "boolean") {
    return answer.toString() === requiredAnswer || normalizeAnswerToken(answer.toString()) === normalizeAnswerToken(requiredAnswer);
  }

  if (answer === requiredAnswer) return true;

  const a = normalizeAnswerToken(answer);
  const r = normalizeAnswerToken(requiredAnswer);
  if (a === r) return true;

  const YES = new Set(["yes", "si", "true"]);
  const NO = new Set(["no", "false"]);
  if (YES.has(a) && YES.has(r)) return true;
  if (NO.has(a) && NO.has(r)) return true;

  return false;
}

function getAnswerComparable(
  answer: QuestionnaireAnswers[string]
): string | boolean | string[] | undefined {
  if (answer === undefined || answer === null) return undefined;
  if (typeof answer === "object" && !Array.isArray(answer) && "option" in answer) {
    return answer.option;
  }
  return answer as string | boolean | string[];
}

/**
 * Evaluates if a rule's requirements are met based on current answers
 */
export function evaluateRule(
  rule: QuestionnaireRule,
  answers: QuestionnaireAnswers
): boolean {
  const { type, requirements } = rule;

  const results = requirements.map((req) => {
    const comparable = getAnswerComparable(answers[req.required_question_id]);

    if (comparable === undefined) return false;

    // Handle array answers (multiple_option)
    if (Array.isArray(comparable)) {
      return comparable.some((v) => answerMatchesRequired(v, req.required_answer));
    }

    return answerMatchesRequired(comparable, req.required_answer);
  });

  // Apply AND/OR logic
  if (type === "and") {
    return results.every((result) => result === true);
  } else {
    return results.some((result) => result === true);
  }
}

/**
 * Determines if a question should be visible based on its rules
 */
export function shouldShowQuestion(
  question: QuestionnaireQuestion,
  answers: QuestionnaireAnswers
): boolean {
  // If no rules, always show
  if (!question.rules || question.rules.length === 0) {
    return true;
  }

  // Evaluate all rules
  const ruleResults = question.rules.map((rule) => evaluateRule(rule, answers));

  // Apply the question's rule_type (and/or)
  if (question.rule_type === "and") {
    return ruleResults.every((result) => result === true);
  } else {
    return ruleResults.some((result) => result === true);
  }
}

/**
 * Gets the list of visible questions in order
 */
export function getVisibleQuestions(
  allQuestions: QuestionnaireQuestion[],
  answers: QuestionnaireAnswers
): QuestionnaireQuestion[] {
  return allQuestions
    .filter((q) => q.is_visible && shouldShowQuestion(q, answers))
    .sort((a, b) => a.order - b.order);
}

/**
 * Validates if an answer is provided for a question
 */
export function isQuestionAnswered(
  question: QuestionnaireQuestion,
  answers: QuestionnaireAnswers
): boolean {
  // Informational questions don't require answers
  if (question.type === "informational") {
    return true;
  }

  // Even optional questions require an answer to enable continue button
  const answer = answers[question.partner_questionnaire_question_id];

  if (answer === undefined || answer === null || answer === "") {
    return false;
  }

  // Check for empty arrays in multiple_option
  if (Array.isArray(answer) && answer.length === 0) {
    return false;
  }

  // For single_option_with_textarea, check if textarea is required and filled
  if (question.type === "single_option_with_textarea" && typeof answer === 'object' && answer !== null) {
    const answerObj = answer as { option: string; textarea: string };
    // Find the selected option to check if it requires additional field
    const selectedOption = question.options.find(opt => opt.option === answerObj.option);
    if (selectedOption?.is_show_additional_field) {
      // If the selected option shows a textarea, it must have content
      if (!answerObj.textarea || answerObj.textarea.trim() === '') {
        return false;
      }
    }
  }

  return true;
}

export type QuestionnaireAnswerValue =
  | string
  | string[]
  | boolean
  | { option: string; textarea: string };

/**
 * Whether selecting this value should immediately advance (no Continuar tap).
 * single_option_with_textarea only auto-advances when the option has no follow-up field.
 * Multi-select (arrays / multiple_option) only auto-advances when "None of the above"
 * is the sole selection — any other combination still requires Continuar.
 */
export function shouldAutoAdvanceOnAnswer(
  question: QuestionnaireQuestion | null | undefined,
  value: QuestionnaireAnswerValue | undefined
): boolean {
  if (!question || question.has_next_button === false) return false;

  if (question.type === "multiple_option") {
    if (!Array.isArray(value)) return false;
    return isOnlyNoneOfTheAboveSelected(value, getNoneOfTheAboveValues(question.options));
  }

  // Non-multiple_option arrays: unchanged, never auto-advance
  if (Array.isArray(value)) return false;
  if (value === undefined || value === null || value === "") return false;

  if (question.type === "boolean") {
    return typeof value === "boolean";
  }

  if (question.type === "single_option") {
    return typeof value === "string";
  }

  if (question.type === "single_option_with_textarea") {
    if (typeof value !== "object" || value === null || !("option" in value)) {
      return false;
    }
    const selectedOption = question.options.find((opt) => opt.option === value.option);
    return !!selectedOption && !selectedOption.is_show_additional_field;
  }

  return false;
}

/**
 * Single-choice questions hide Continuar so a tap auto-advances.
 * Multi-select hides it too, but only once "None of the above" is the sole selection.
 * Show Continuar again whenever the question already has an answer sitting on it — whether
 * arrived at via Back, or via Forward after going back further and changing an earlier answer
 * (re-selecting an already-checked option doesn't re-fire auto-advance, so without this the
 * user would otherwise be stuck with no way to move on) — or when single_option_with_textarea
 * needs the follow-up field filled first.
 */
export function shouldHideContinueForAutoAdvance(
  question: QuestionnaireQuestion | null | undefined,
  answers: QuestionnaireAnswers
): boolean {
  if (!question) return false;

  const answer = answers[question.partner_questionnaire_question_id];

  const isSingleAutoAdvanceType =
    question.type === "boolean" ||
    question.type === "single_option" ||
    question.type === "single_option_with_textarea";
  const isNoneSelectedMultiSelect =
    question.type === "multiple_option" &&
    isOnlyNoneOfTheAboveSelected(
      Array.isArray(answer) ? answer : undefined,
      getNoneOfTheAboveValues(question.options)
    );

  if (!isSingleAutoAdvanceType && !isNoneSelectedMultiSelect) return false;
  if (question.has_next_button === false) return false;

  // Option that reveals a required textarea — Continuar stays until they finish
  if (
    question.type === "single_option_with_textarea" &&
    typeof answer === "object" &&
    answer !== null &&
    "option" in answer
  ) {
    const selectedOption = question.options.find((opt) => opt.option === answer.option);
    if (selectedOption?.is_show_additional_field) {
      return false;
    }
  }

  // Already-answered — show Continuar so they can go forward without re-tapping an option
  // that's already selected (re-tapping it wouldn't re-fire auto-advance anyway)
  if (isQuestionAnswered(question, answers)) {
    return false;
  }

  return true;
}

/**
 * Checks if all visible questions have been answered
 */
export function isQuestionnaireComplete(
  allQuestions: QuestionnaireQuestion[],
  answers: QuestionnaireAnswers
): boolean {
  const visibleQuestions = getVisibleQuestions(allQuestions, answers);

  return visibleQuestions.every((question) =>
    isQuestionAnswered(question, answers)
  );
}

/**
 * Gets the index of the current question in the visible questions list
 */
export function getCurrentQuestionIndex(
  visibleQuestions: QuestionnaireQuestion[],
  currentQuestionId: string
): number {
  return visibleQuestions.findIndex(
    (q) => q.partner_questionnaire_question_id === currentQuestionId
  );
}
